import { Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OfflineChangesService {
  private readonly destroy$ = new Subject<void>();
  storeName: string = "offline_changes"
  constructor(private dbService: NgxIndexedDBService) { }

  public async reset(): Promise<void> {
    await firstValueFrom(this.dbService.clear(this.storeName));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async addItem(element: string, url: string, method: string, parameters: any, file?: File): Promise<void> {
    const offlineChange: OfflineChange = {
      element,
      url,
      method,
      parameters: JSON.stringify(parameters),
      file
    };

    try {
      await firstValueFrom(this.dbService.add(this.storeName, offlineChange));
    } catch (error) {
      console.error('Error adding offline change:', error);
      throw error;
    }
  }

  public async addFile(key: string, file: File): Promise<void> {
    try {
      await firstValueFrom(this.dbService.add(this.storeName, file, key));
    } catch (error) {
      console.error('Error adding file to offline changes:', error);
      throw error;
    }
  }

  public async getItem(key: string): Promise<OfflineChange> {
    return await firstValueFrom(this.dbService.getByID<OfflineChange>(this.storeName, key));
  }

  public async getAll(): Promise<OfflineChange[]> {
    try {
      return await firstValueFrom(
        this.dbService.getAll<OfflineChange>(this.storeName)
      );
    } catch (error) {
      console.error('Error getting offline changes:', error);
      throw error;
    }
  }

  public async getCount(): Promise<number> {
    try {
      return await firstValueFrom(this.dbService.count(this.storeName));
    } catch (error) {
      console.error('Error getting offline changes count:', error);
      throw error;
    }
  }

  /**
   * Generates a hash from the entire OfflineChange object (minus id)
   * SHA-256 is deterministic (same input = same hash) and has extremely low collision probability!
   * @param change The offline change to hash
   * @returns SHA-256 hash of the entire change object
   */
  private async generateHash(change: OfflineChange): Promise<string> {
    // Create a copy of the change object without the id
    const { id, ...changeWithoutId } = change;
    const key = JSON.stringify(changeWithoutId);

    // Use Web Crypto API to generate SHA-256 hash
    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generates a quick FNV-1a hash (synchronous) for initial deduplication scan
   * @param change The offline change to hash
   * @returns FNV-1a hash as a string
   */
  private generateQuickHash(change: OfflineChange): string {
    // Create a copy of the change object without the id
    const { id, ...changeWithoutId } = change;
    const key = JSON.stringify(changeWithoutId);

    // FNV-1a hash implementation (synchronous)
    let hash = 0x811c9dc5; // FNV offset basis
    const prime = 0x01000193; // FNV prime

    for (let i = 0; i < key.length; i++) {
      hash ^= key.charCodeAt(i);
      hash = (hash * prime) >>> 0; // Keep as 32-bit unsigned
    }

    return hash.toString(16);
  }

  /**
   * Streams entries from database without loading all into memory
   * Uses direct IndexedDB access with synchronous processing to avoid transaction timeouts
   * @param callback Function called for each entry
   */
  public async streamEntries(callback: (change: OfflineChange) => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('PhDsoft_Offline');

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const cursorRequest = store.openCursor();

        cursorRequest.onerror = () => reject(cursorRequest.error);

        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (cursor) {
            const change = cursor.value as OfflineChange;
            callback(change);
            cursor.continue();
          } else {
            db.close();
            resolve();
          }
        };
      };
    });
  }

  /**
   * Two-phase hybrid hashing deduplication system
   * Phase 1: Quick scan with FNV-1a hash, storing only database keys
   * Phase 2: Crypto verification with SHA-256 for potential duplicates
   * @param deduplicationRules Rules for deduplication
   */
  public async deduplicateWithHashedKeys(deduplicationRules: DeduplicationRule[]): Promise<void> {
    // Phase 1: Quick scan with FNV-1a hash
    const quickHashGroups = new Map<string, number[]>();

    await this.streamEntries(async (change: OfflineChange) => {
      // Check if this change should be deduplicated
      const shouldDeduplicate = deduplicationRules.some(rule =>
        change.element === rule.elementType && change.url.includes(rule.baseUrl)
      );

      if (shouldDeduplicate) {
        // Generate quick hash (synchronous)
        const quickHash = this.generateQuickHash(change);

        // Store database key in the quick hash group
        if (!quickHashGroups.has(quickHash)) {
          quickHashGroups.set(quickHash, []);
        }
        quickHashGroups.get(quickHash)!.push(Number(change.id));
      }
    });

    console.log(`Offline Sync: Found ${quickHashGroups.size} unique quick hashes`);

    // Phase 2: Crypto verification for groups with potential duplicates
    for (const [quickHash, keys] of quickHashGroups) {
      if (keys.length > 1) {
        console.log(`Offline Sync: Checking ${keys.length} entries with quick hash ${quickHash}`);

        // Use the same pattern: hash as key, database keys as values
        const sha256Groups = new Map<string, number[]>();

        for (const key of keys) {
          const entry = await firstValueFrom(this.dbService.getByID<OfflineChange>(this.storeName, key));

          if (entry) {
            const sha256Hash = await this.generateHash(entry);

            if (!sha256Groups.has(sha256Hash)) {
              sha256Groups.set(sha256Hash, []);
            }
            sha256Groups.get(sha256Hash)!.push(key);
          }
        }

        // Process duplicates based on keys only
        for (const [sha256Hash, duplicateKeys] of sha256Groups) {
          if (duplicateKeys.length > 1) {
            // console.log(`Found ${duplicateKeys.length} duplicates with SHA-256 ${sha256Hash}`);

            // Find the maximum ID (last occurrence)
            const maxId = Math.max(...duplicateKeys);

            // Remove all except the maximum ID
            for (const keyToRemove of duplicateKeys) {
              if (keyToRemove !== maxId) {
                await this.removeItem(keyToRemove.toString());
                // console.log(`Removed duplicate entry: ${keyToRemove}`);
              }
            }
          }
        }
      }
    }

    console.log('Offline Sync: Deduplication finished');
  }

  public async removeItem(key: string): Promise<void> {
    try {
      await firstValueFrom(this.dbService.delete(this.storeName, key));
    } catch (error) {
      console.error('Error removing offline change:', error);
      throw error;
    }
  }

  /**
   * Gets all keys from the database synchronously using streamEntries
   * @returns Array of all offline change keys
   */
  public async getAllKeys(): Promise<string[]> {
    const keys: string[] = [];

    await this.streamEntries(async (change: OfflineChange) => {
        if (change.id) {
            keys.push(change.id);
        }
    });

    return keys;
  }

}

export interface OfflineChange {
  id?: string;
  element: string,
  url: string,
  method: string,
  parameters: string,
  file?: File
}

export interface DeduplicationRule {
  elementType: string;
  baseUrl: string;
}
